import _ from 'lodash';

import {
  LoanApplicationTabName,
  LoanNavigationConfig,
  TasksSubtab,
} from 'constants/loanApplication';

import {
  EDIT_ENTITY_DEFAULT_SECTION,
  ENTITY_TYPE_HREF,
} from '../../tabs/borrowers/EditEntityModal/EditEntityModal.constants';
import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_ACTION_TYPE,
  NEW_RECORD_DATA,
} from '../../tabs/financialInfo/FinancialInfo.constants';
import {
  ADD_MORTGAGE_HREF,
  ENTITY_MODAL_TITLE_MAP,
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
  PROPERTY_AND_LOAN_ENTITY_TYPES,
} from '../../tabs/propertyAndLoan/EditModal/EditModal.constants';
import { NEW_RECORD_ID } from '../../tabs/propertyAndLoan/PropertyAndLoan.contacts';
import { SearchCategory } from '../UniversalSearch.constants';
import { buildFinancialRecordQuickActions } from '../UniversalSearch.financialHelpers';

import {
  BorrowerEditSectionName,
  EntityDrawerSectionNameMap,
  FinancialInfoViewName,
  UnderwritingSubtabName,
} from './navigation.constants';

const getAvailableSubtabNames = availableTabs => {
  if (_.isEmpty(availableTabs)) {
    return null;
  }

  return Object.values(availableTabs).reduce((names, category) => {
    if (!category.subtabs) {
      return names;
    }

    Object.values(category.subtabs).forEach(subtab => {
      if (subtab.tabName) {
        names.add(subtab.tabName);
      }

      if (subtab.customName) {
        names.add(subtab.customName);
      }
    });

    return names;
  }, new Set());
};

const isTabAvailable = ({ tabName, availableSubtabNames }) => {
  if (!availableSubtabNames) {
    return true;
  }

  return availableSubtabNames.has(tabName);
};

export const buildNavigationIndex = ({
  availableTabs,
  borrowers = [],
  liabilities = [],
  incomes = [],
  assets = [],
  properties = [],
} = {}) => {
  const items = [];
  const availableSubtabNames = getAvailableSubtabNames(availableTabs);

  Object.values(LoanNavigationConfig).forEach(category => {
    if (!category.subtabs) {
      return;
    }

    Object.values(category.subtabs).forEach(subtab => {
      const displayName = subtab.customName || subtab.tabName;
      const tabRoute = _.kebabCase(subtab.tabName);

      if (!isTabAvailable({ tabName: displayName, availableSubtabNames })) {
        return;
      }

      items.push({
        id: `nav-${tabRoute}`,
        label: displayName,
        category: SearchCategory.NAVIGATION,
        breadcrumb: category.customName || category.tabName,
        tabRoute,
        isTabLink: true,
      });
    });
  });

  const propertyAndLoanName = LoanApplicationTabName.PROPERTY_AND_LOAN;
  const isPropertyAndLoanAvailable = isTabAvailable({
    tabName: propertyAndLoanName,
    availableSubtabNames,
  });

  if (isPropertyAndLoanAvailable) {
    const propertyAndLoanTabRoute = _.kebabCase(propertyAndLoanName);

    PROPERTY_AND_LOAN_ENTITY_TYPES.forEach(entityHref => {
      const entityTitle = ENTITY_MODAL_TITLE_MAP[entityHref];

      items.push({
        id: `nav-drawer-${entityHref}`,
        label: `Edit ${entityTitle}`,
        category: SearchCategory.NAVIGATION,
        breadcrumb: propertyAndLoanName,
        tabRoute: propertyAndLoanTabRoute,
        entityType: entityHref,
        isQuickAction: true,
        quickActionContext: null,
      });

      const sectionNameMap = EntityDrawerSectionNameMap[entityHref];

      if (sectionNameMap) {
        Object.entries(sectionNameMap).forEach(([sectionHref, sectionName]) => {
          items.push({
            id: `nav-section-${entityHref}-${sectionHref}`,
            label: sectionName,
            category: SearchCategory.NAVIGATION,
            breadcrumb: `${propertyAndLoanName} > ${entityTitle}`,
            tabRoute: propertyAndLoanTabRoute,
            entityType: entityHref,
            sectionHref,
            isQuickAction: true,
            quickActionContext: entityHref,
          });
        });
      }
    });
  }

  const financialInfoName = LoanApplicationTabName.FINANCIAL_INFO;
  const isFinancialInfoAvailable = isTabAvailable({
    tabName: financialInfoName,
    availableSubtabNames,
  });

  if (isFinancialInfoAvailable) {
    const financialInfoTabRoute = _.kebabCase(financialInfoName);

    Object.entries(FinancialInfoViewName).forEach(([viewPath, viewName]) => {
      items.push({
        id: `nav-financial-${viewPath}`,
        label: viewName,
        category: SearchCategory.NAVIGATION,
        breadcrumb: financialInfoName,
        tabRoute: financialInfoTabRoute,
        subtabRoute: viewPath,
        isQuickAction: true,
        quickActionContext: null,
      });
    });

    const financialRecordQuickActions = buildFinancialRecordQuickActions({
      tabName: financialInfoName,
      tabRoute: financialInfoTabRoute,
      liabilities,
      incomes,
      assets,
      properties,
    });

    items.push(...financialRecordQuickActions);
  }

  const underwritingName = LoanApplicationTabName.UNDERWRITING;
  const isUnderwritingAvailable = isTabAvailable({
    tabName: underwritingName,
    availableSubtabNames,
  });

  if (isUnderwritingAvailable) {
    UnderwritingSubtabName.forEach(({ subtabRoute, label }) => {
      items.push({
        id: `nav-uw-${subtabRoute}`,
        label,
        category: SearchCategory.NAVIGATION,
        breadcrumb: underwritingName,
        tabRoute: _.kebabCase(underwritingName),
        subtabRoute,
        isQuickAction: true,
        quickActionContext: null,
      });
    });
  }

  const tasksName = LoanApplicationTabName.TASKS;
  const isTasksAvailable = isTabAvailable({
    tabName: tasksName,
    availableSubtabNames,
  });

  if (isTasksAvailable) {
    Object.values(TasksSubtab).forEach(subtab => {
      items.push({
        id: `nav-tasks-${subtab.tabRoute}`,
        label: subtab.tabName,
        category: SearchCategory.NAVIGATION,
        breadcrumb: tasksName,
        tabRoute: _.kebabCase(tasksName),
        subtabRoute: subtab.tabRoute,
        isQuickAction: true,
        quickActionContext: null,
      });
    });
  }

  const borrowersName = LoanApplicationTabName.BORROWERS;
  const isBorrowersAvailable = isTabAvailable({
    tabName: borrowersName,
    availableSubtabNames,
  });

  if (isBorrowersAvailable) {
    const borrowersTabRoute = _.kebabCase(borrowersName);

    borrowers.forEach(borrower => {
      const borrowerName = borrower.fullName || 'Borrower';

      items.push({
        id: `nav-borrower-edit-${borrower.borrowerId}`,
        label: `Edit ${borrowerName}`,
        category: SearchCategory.NAVIGATION,
        breadcrumb: borrowersName,
        tabRoute: borrowersTabRoute,
        borrowerEntityType: ENTITY_TYPE_HREF.BORROWER,
        borrowerEntityId: borrower.borrowerId,
        borrowerDefaultSection: EDIT_ENTITY_DEFAULT_SECTION[ENTITY_TYPE_HREF.BORROWER],
        isQuickAction: true,
        quickActionContext: null,
      });

      BorrowerEditSectionName.forEach(({ sectionHref, label }) => {
        items.push({
          id: `nav-borrower-section-${borrower.borrowerId}-${sectionHref}`,
          label,
          category: SearchCategory.NAVIGATION,
          breadcrumb: `${borrowersName} > ${borrowerName}`,
          tabRoute: borrowersTabRoute,
          borrowerEntityType: ENTITY_TYPE_HREF.BORROWER,
          borrowerEntityId: borrower.borrowerId,
          borrowerDefaultSection: sectionHref,
          isQuickAction: true,
          quickActionContext: borrower.borrowerId,
        });
      });
    });
  }

  return items;
};

export const buildQuickActionsIndex = ({
  availableTabs,
  canAddAdditionalMortgage = false,
  canAddContact = false,
} = {}) => {
  const items = [];
  const availableSubtabNames = getAvailableSubtabNames(availableTabs);

  const propertyAndLoanName = LoanApplicationTabName.PROPERTY_AND_LOAN;

  if (isTabAvailable({ tabName: propertyAndLoanName, availableSubtabNames })) {
    const tabRoute = _.kebabCase(propertyAndLoanName);

    if (canAddAdditionalMortgage) {
      items.push({
        id: 'quick-property-and-loan-add-additional-mortgage',
        label: 'Add Additional Mortgage',
        category: SearchCategory.NAVIGATION,
        breadcrumb: `${propertyAndLoanName} > Loan & Lock`,
        tabRoute,
        entityType: ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK,
        sectionHref: LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING,
        section: ADD_MORTGAGE_HREF,
        isQuickAction: true,
        quickActionContext: null,
      });
    }
  }

  const contactsName = LoanApplicationTabName.CONTACTS;

  if (canAddContact && isTabAvailable({ tabName: contactsName, availableSubtabNames })) {
    items.push({
      id: 'quick-contacts-add-contact',
      label: 'Add Contact',
      category: SearchCategory.NAVIGATION,
      breadcrumb: contactsName,
      tabRoute: _.kebabCase(contactsName),
      entityType: ENTITY_TYPE_HREF_MAP.CONTACTS,
      entityId: NEW_RECORD_ID,
      isQuickAction: true,
      quickActionContext: null,
    });
  }

  const financialInfoName = LoanApplicationTabName.FINANCIAL_INFO;

  if (isTabAvailable({ tabName: financialInfoName, availableSubtabNames })) {
    const tabRoute = _.kebabCase(financialInfoName);

    Object.entries(FinancialInfoViewName).forEach(([viewPath, viewName]) => {
      items.push({
        id: `quick-financial-info-add-${viewPath}`,
        label: `Add ${viewName}`,
        category: SearchCategory.NAVIGATION,
        breadcrumb: financialInfoName,
        tabRoute,
        subtabRoute: viewPath,
        entityId: ALL_BORROWERS_PATH,
        section: FINANCIAL_INFO_ACTION_TYPE.EDIT,
        subsection: NEW_RECORD_DATA.id,
        isQuickAction: true,
        quickActionContext: null,
      });
    });
  }

  return items;
};

export const buildShortcutMap = ({ availableTabs }) => {
  if (_.isEmpty(availableTabs)) {
    return { shortcutMap: {}, tabNameToShortcut: {} };
  }

  const availableSubtabNames = getAvailableSubtabNames(availableTabs);
  const MAX_SHORTCUTS = 9;

  const orderedTabs = Object.values(LoanNavigationConfig)
    .filter(category => category.subtabs)
    .flatMap(category => Object.values(category.subtabs))
    .filter(subtab => {
      const tabName = subtab.customName || subtab.tabName;

      return isTabAvailable({ tabName, availableSubtabNames });
    })
    .slice(0, MAX_SHORTCUTS);

  return orderedTabs.reduce(
    (acc, subtab, index) => {
      const displayName = subtab.customName || subtab.tabName;
      const tabRoute = _.kebabCase(subtab.tabName);
      const shortcutKey = String(index + 1);

      acc.shortcutMap[shortcutKey] = { tabRoute, tabName: displayName };
      acc.tabNameToShortcut[displayName] = shortcutKey;

      return acc;
    },
    { shortcutMap: {}, tabNameToShortcut: {} },
  );
};
